import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { useModerationCounts } from "@/hooks/use-moderation-counts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Home, Menu, User, ChevronRight, type LucideIcon } from "lucide-react";
import { NAV_SECTIONS, BOTTOM_TAB_SECTION_COUNT, activeTabKey, type NavSectionRoute } from "@/lib/nav-sections";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { PartyPopper } from "lucide-react";
import { useArenaNavVisible } from "@/components/ArenaHomeTile";
import { ARENA_COPY } from "@/lib/arena-copy";

type Tab = {
  key: string;
  to: "/home" | NavSectionRoute;
  search?: { experts?: boolean };
  label: string;
  Icon: LucideIcon;
};

const tabs: Tab[] = [
  { key: "home", to: "/home", label: "Главная", Icon: Home },
  ...NAV_SECTIONS.slice(0, BOTTOM_TAB_SECTION_COUNT).map((s) => ({ key: s.key, to: s.to!, search: s.search, label: s.label, Icon: s.Icon })),
];

type MoreItem = {
  to?: NavSectionRoute | "/profile" | "/arena/people";
  search?: { experts?: boolean };
  label: string;
  Icon: LucideIcon;
  soon?: boolean;
  href?: string;
};

const MORE_ITEMS: MoreItem[] = [
  ...NAV_SECTIONS.slice(BOTTOM_TAB_SECTION_COUNT),
  { to: "/profile", label: "Профиль", Icon: User },
];


export function BottomTabBar() {
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const expertsMode = useRouterState({ select: (s) => !!(s.location.search as { experts?: unknown }).experts });
  const activeKey = activeTabKey(pathname, expertsMode);
  const moreActive = activeKey === "more";
  const counts = useModerationCounts();
  const arenaVisible = useArenaNavVisible();
  const moreItems: MoreItem[] = arenaVisible
    ? [{ to: "/arena/people", label: ARENA_COPY.navLabel, Icon: PartyPopper }, ...MORE_ITEMS]
    : MORE_ITEMS;
  const { user } = useAuth();
  const { data: coffeePending = 0 } = useQuery({
    queryKey: ["coffee-invites", "mine"],
    enabled: !!user,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_coffee_invites");
      if (error) throw error;
      return data ?? [];
    },
    select: (rows: any[]) => rows.filter((r) => r.direction === "incoming" && !r.responded_at).length,
  });
  const badgeFor = (to: Tab["to"]) =>
    to === "/events" ? counts.events : to === "/requests" ? counts.requests : 0;

  return (
    <>
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-td-line bg-white shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.08)] md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label="Основная навигация"
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around">
        {tabs.map(({ key, to, search, label, Icon }) => {
          const n = counts.isAdmin ? badgeFor(to) : 0;
          return (
          <li key={key} className="flex-1">
            <Link
              to={to}
              search={(search ?? {}) as never}
              data-active={activeKey === key ? "true" : undefined}
              className="flex flex-col items-center gap-1 py-2 text-[11px] font-medium text-td-muted transition data-[active=true]:text-td-orange"
            >
              <span className="relative">
                <Icon size={22} strokeWidth={2} />
                {n > 0 && (
                  <span className="absolute -right-2 -top-1 h-4 min-w-4 rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-4 text-white">
                    {n > 9 ? "9+" : n}
                  </span>
                )}
              </span>
              <span className="whitespace-nowrap">{label}</span>
            </Link>
          </li>
          );
        })}
        <li className="flex-1">
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={
              "flex w-full flex-col items-center gap-1 py-2 text-[11px] font-medium transition " +
              (moreActive ? "text-td-orange" : "text-td-muted")
            }
          >
            <span className="relative">
              <Menu size={22} strokeWidth={2} />
              {coffeePending > 0 && <span className="absolute -right-1 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500" />}
            </span>
            <span className="whitespace-nowrap">Ещё</span>
          </button>
        </li>
      </ul>
    </nav>

    <div className="md:hidden">
      <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
        <DrawerContent
          overlayClassName="bg-black/40"
          className="rounded-t-2xl border-0 bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.25)]"
        >
          <DrawerTitle className="mb-3 mt-3 text-left text-lg font-semibold text-td-dark">
            Ещё разделы
          </DrawerTitle>
          <div className="flex flex-col gap-1">
            {moreItems.map((item) => {
              const inner = (
                <>
                  <span className={"flex h-10 w-10 shrink-0 items-center justify-center rounded-full " + (item.soon ? "bg-td-soft" : "bg-td-teal")}>
                    <item.Icon className={"h-5 w-5 " + (item.soon ? "text-td-muted" : "text-white")} />
                  </span>
                  <span className="min-w-0 flex-1 text-sm font-medium text-td-dark">{item.label}</span>
                  {item.to === "/coffee" && coffeePending > 0 && (
                    <span className="h-5 min-w-5 shrink-0 rounded-full bg-red-500 px-1.5 text-center text-[11px] font-bold leading-5 text-white">
                      {coffeePending > 9 ? "9+" : coffeePending}
                    </span>
                  )}
                  {item.soon ? (
                    <span className="shrink-0 rounded-full bg-td-soft px-2 py-0.5 text-[10px] font-medium text-td-muted">Скоро</span>
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-td-muted" />
                  )}
                </>
              );
              if (item.href) {
                return (
                  <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => setMoreOpen(false)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition hover:bg-td-soft/60 active:bg-td-soft">
                    {inner}
                  </a>
                );
              }
              if (item.soon || !item.to) {
                return (
                  <div key={item.label} className="flex items-center gap-3 rounded-xl px-2 py-2.5 opacity-55">
                    {inner}
                  </div>
                );
              }
              const destination = item.to;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    navigate({ to: destination, search: (item.search ?? {}) as never });
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition hover:bg-td-soft/60 active:bg-td-soft"
                >
                  {inner}
                </button>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
    </>
  );
}
